/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Aui 기본 언어팩 클래스를 정의한다.
 *
 * @file /src/utils/Language.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 10. 8.
 */
export interface LanguageProvider {
    getText(): Promise<string>;
}

export type LanguageText = string | Promise<string> | LanguageProvider;

export interface Properties {
    /**
     * 탐색할 언어코드
     */
    code: string;

    /**
     * 언어팩 문자열 치환자
     */
    placeHolder?: { [key: string]: string };

    /**
     * 언어팩을 탐색할 경로
     */
    paths?: string[];

    /**
     * 탐색할 ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등)
     */
    languages?: string[];
}

export class Language implements LanguageProvider {
    /**
     * 언어팩 탐색을 시작할 기본경로
     */
    static base: string = './assets';

    /**
     * 기본적으로 탐색할 언어팩 경로
     */
    static defaultPath: string = '/languages';

    /**
     * 기본적으로 탐색할 ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등)
     */
    static defaultLangauge: string;

    /**
     * 동일설정 언어팩 객체의 캐시
     */
    static caches: Map<string, string | { [key: string]: string | object }> = new Map();

    /**
     * 언어팩 캐시
     */
    static packs: Map<string, { [key: string]: string | object }> = new Map();

    /**
     * 진행중인 언어팩 요청 (동일 URL 중복 요청 방지)
     */
    static fetching: Map<string, Promise<{ [key: string]: string | object } | null>> = new Map();

    /**
     * 탐색할 언어코드
     */
    code: string;

    /**
     * 언어팩 문자열 치환자
     */
    placeHolder: { [key: string]: string } | null;

    /**
     * 언어팩을 탐색할 경로
     */
    paths: string[];

    /**
     * 탐색할 ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등)
     */
    languages: string[];

    /**
     * 언어팩 객체를 정의한다.
     *
     * @param properties - 언어팩 설정
     */
    constructor(properties: Properties) {
        this.code = properties.code;
        this.placeHolder = properties.placeHolder ?? null;
        this.paths = [...(properties.paths ?? []), Language.getDefaultPath()];
        this.languages = [
            ...new Set([...(properties.languages ?? []), Language.getDefaultLangauge(), 'en', 'ko']),
        ];
    }

    /**
     * 언어팩 탐색을 시작할 기본경로를 지정한다.
     *
     * @param base - 언어팩 탐색을 시작할 기본경로
     */
    static setBase(base: string): void {
        Language.base = base;
    }

    /**
     * 언어팩 탐색을 시작할 기본경로를 가져온다.
     *
     * @returns 언어팩 탐색을 시작할 기본경로
     */
    static getBase(): string {
        return Language.base ?? '';
    }

    /**
     * 언어팩 기본폴더를 지정한다.
     *
     * @param defaultPath - 언어팩 기본폴더
     */
    static setDefaultPath(defaultPath: string): void {
        Language.defaultPath = defaultPath;
    }

    /**
     * 언어팩 기본폴더를 지정한다.
     *
     * @returns 언어팩 기본폴더
     */
    static getDefaultPath(): string {
        return Language.defaultPath ?? '/languages';
    }

    /**
     * 기본으로 탐색할 ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등) 를 지정한다.
     *
     * @param defaultLangauge - 기본언어코드
     */
    static setDefaultLangauge(defaultLangauge: string): void {
        Language.defaultLangauge = defaultLangauge;
    }

    /**
     * 기본으로 탐색할 ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등) 를 가져온다.
     *
     * @returns {string} 기본언어코드
     */
    static getDefaultLangauge(): string {
        Language.defaultLangauge ??=
            document.querySelector(':root').getAttribute('data-language') ??
            Language.fromTag(document.querySelector(':root').getAttribute('lang') ?? 'ko');

        return Language.defaultLangauge;
    }

    /**
     * 표준 언어태그를 언어팩 언어코드로 변환한다. (zh-TW, zh-Hant → zt / en-US → en)
     *
     * @param tag - 표준 언어태그
     * @returns 언어팩 언어코드
     */
    static fromTag(tag: string): string {
        tag = tag.trim().toLowerCase();

        // 중국어 번체(대만/홍콩/마카오)는 zt 로 처리한다.
        if (/^zh-(tw|hk|mo|hant)(-|$)/.test(tag) == true) {
            return 'zt';
        }

        return tag.split('-')[0];
    }

    /**
     * 언어팩 객체를 가져온다.
     *
     * @param code - 언어팩코드
     * @param placeHolder - 치환자
     * @param paths - 언어팩을 탐색할 경로
     * @param languages - ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등)
     * @returns 언어팩 객체
     */
    static get(
        code: string,
        placeHolder: { [key: string]: string } = null,
        paths: string[] = null,
        languages: string[] = null
    ): Language {
        return new Language({ code, placeHolder, paths, languages });
    }

    /**
     * 에러코드 언어팩 객체를 가져온다.
     *
     * @param code - 에러코드
     * @param placeHolder - 치환자
     * @param paths - 언어팩을 탐색할 경로
     * @param languages - ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등)
     * @returns 언어팩 객체
     */
    static getError(
        code: string,
        placeHolder: { [key: string]: string } = null,
        paths: string[] = null,
        languages: string[] = null
    ): Language {
        return new Language({ code: 'errors.' + code, placeHolder, paths, languages });
    }

    /**
     * 액션코드 언어팩 객체를 가져온다.
     *
     * @param code - 액션코드
     * @param placeHolder - 치환자
     * @param paths - 언어팩을 탐색할 경로
     * @param languages - ISO 639-1 에 따른 2자리 알파벳 언어코드 (en, ko 등)
     * @returns 언어팩 객체
     */
    static getAction(
        code: string,
        placeHolder: { [key: string]: string } = null,
        paths: string[] = null,
        languages: string[] = null
    ): Language {
        return new Language({ code: 'actions.' + code, placeHolder, paths, languages });
    }

    /**
     * 언어팩 파일을 비동기로 불러온다.
     *
     * @param path - 불러올 언어팩 경로
     * @param language - 언어코드
     * @param retry - 재시도횟수
     * @returns 언어팩
     */
    private async load(path: string, language: string): Promise<{ [key: string]: string | object } | null> {
        const url: string = Language.getBase() + path + '/' + language + '.json';
        if (Language.packs.has(url) == true) {
            return Language.packs.get(url);
        }

        if (Language.fetching.has(url) == true) {
            return Language.fetching.get(url);
        }

        const promise = (async () => {
            try {
                for (let attempt = 0; attempt < 3; attempt++) {
                    if (attempt > 0) {
                        await this.sleep();
                    }
                    try {
                        const response = await fetch(url);
                        if (response.ok == false) {
                            throw new Error('HTTP ' + response.status);
                        }
                        const pack = (await response.json()) as { [key: string]: string | object };
                        Language.packs.set(url, pack);
                        return pack;
                    } catch (e) {
                        if (attempt == 2) {
                            return null;
                        }
                    }
                }

                return null;
            } finally {
                Language.fetching.delete(url);
            }
        })();

        Language.fetching.set(url, promise);

        return promise;
    }

    /**
     * 언어팩 문자열에서 템플릿 치환자를 실제 데이터로 변환한다.
     *
     * @param text - 언어팩 문자열
     * @returns 치환된 메시지
     */
    private replacePlaceHolder(text: string): string {
        if (this.placeHolder == null || text.includes('${') == false) {
            return text;
        }

        const templets = [...text.matchAll(/\$\{(.*?)\}/g)];
        templets.forEach(([templet, key]) => {
            text = text.replace(templet, this.placeHolder[key] ?? '');
        });

        return text;
    }

    /**
     * 언어팩을 가져온다.
     * 언어코드 탐색결과가 object 일 경우, object 를 그대로 반환하여 재탐색할 수 있도록 한다.
     *
     * @returns 언어팩
     */
    async getRaw(): Promise<string | { [key: string]: string | object }> {
        /**
         * 이미 탐색한 언어팩이 존재한다면 해당 값을 반환한다.
         * placeHolder 한 결과까지 캐시할 필요는 없으므로 placeHolder 는 제외한다.
         */
        const cache = JSON.stringify({ code: this.code, paths: this.paths, languages: this.languages });
        if (Language.caches.has(cache) == true) {
            return Language.caches.get(cache);
        }

        const keys: string[] = this.code.split('.');

        /**
         * 우선탐색 언어코드 및 언어팩 경로부터 탐색을 시작하여,
         * 언어팩이 존재하지 않을 경우 다음 탐색경로를 탐색한다.
         */
        for (const language of this.languages) {
            for (const path of this.paths) {
                let string: string | { [key: string]: string | object } | null = await this.load(path, language);

                keys.forEach((key) => {
                    if (string === null || string[key] === undefined) {
                        string = null;
                        return false;
                    }

                    string = string[key];
                });

                if (string !== null) {
                    Language.caches.set(cache, string);
                    return string;
                }
            }
        }

        Language.caches.set(cache, this.code);
        return this.code;
    }

    /**
     * 언어팩을 가져온다.
     *
     * @returns 언어팩
     */
    async getText(): Promise<string> {
        const result = await this.getRaw();
        if (typeof result == 'string') {
            return this.replacePlaceHolder(result);
        } else {
            return JSON.stringify(result);
        }
    }

    /**
     * 네트워크 요청 실패시 500ms 대기한다.
     */
    private async sleep(): Promise<void> {
        return new Promise((r) => setTimeout(r, 500));
    }

    /**
     * 언어팩 제공자를 통해 문자열을 가져온다.
     *
     * @param text - 문자열 또는 언어팩 제공자
     * @returns 언어팩 문자열
     */
    static async resolveText(text: LanguageText | undefined): Promise<string> {
        if (typeof text === 'string') {
            return text;
        }

        if (text instanceof Promise) {
            return await text;
        }

        if (text && typeof text.getText === 'function') {
            return await text.getText();
        }

        return '';
    }
}
